import type { GuildBan } from '@discordo/shared';
import { useEffect, useState, type ReactElement } from 'react';

import { ApiError } from '../lib/api';

/**
 * Who is barred from the open server, and the way to let them back.
 *
 * It is a screen of its own rather than a section of the member list because
 * the people on it are exactly the ones that list does not have: banning
 * removes the membership, so without this there would be no way to see a ban,
 * let alone undo one.
 *
 * The list is loaded here and kept here. It is opened rarely and read once,
 * and holding it in the shell would be one more thing to invalidate on every
 * ban, every revoke and every change of server.
 */

const LOAD_FAILURE = 'Não foi possível carregar os banidos. Tente de novo.';
const REVOKE_FAILURE = 'Não foi possível revogar o banimento. Tente de novo.';

export interface BansDialogProps {
  listBans: () => Promise<GuildBan[]>;
  revokeBan: (userId: string) => Promise<void>;
  onClose: () => void;
}

/** What the interface says when the API refused for a reason it stated. */
function describe(cause: unknown, fallback: string): string {
  return cause instanceof ApiError ? cause.message : fallback;
}

/** The date the ban was written, in the reader's own locale. */
function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
}

export function BansDialog({ listBans, revokeBan, onClose }: BansDialogProps): ReactElement {
  const [bans, setBans] = useState<GuildBan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    let live = true;

    listBans()
      .then((list) => {
        if (live) {
          setBans(list);
        }
      })
      .catch((cause: unknown) => {
        if (live) {
          setError(describe(cause, LOAD_FAILURE));
          setBans([]);
        }
      });

    return () => {
      live = false;
    };
  }, [listBans]);

  async function handleRevoke(userId: string): Promise<void> {
    setError(null);
    setRevoking(userId);

    try {
      await revokeBan(userId);
      // Only after the API agreed: a row that disappears on a refusal would
      // tell whoever pressed the button that something happened that did not.
      setBans((previous) => (previous ?? []).filter((ban) => ban.user.id !== userId));
    } catch (cause) {
      setError(describe(cause, REVOKE_FAILURE));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="guild-dialog-backdrop">
      <div className="guild-dialog" role="dialog" aria-label="Banidos">
        <h2 className="guild-dialog-title">Banidos</h2>
        <p className="guild-dialog-hint">
          Quem está aqui não entra no servidor nem com um convite novo. Revogar não devolve a
          pessoa ao servidor — devolve a ela a permissão de aceitar um convite.
        </p>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        {bans === null && <p className="guild-dialog-hint">Carregando…</p>}

        {bans !== null && bans.length === 0 && error === null && (
          <p className="guild-dialog-hint">Ninguém está banido neste servidor.</p>
        )}

        {bans !== null && bans.length > 0 && (
          <ul className="ban-list">
            {bans.map((ban) => (
              <li key={ban.user.id} className="ban-row">
                <div className="ban-who">
                  <span className="member-name">{ban.user.displayName}</span>
                  <span className="ban-note">
                    {ban.reason ?? 'sem motivo registrado'}
                    {' — '}
                    {ban.bannedBy === null
                      ? 'por alguém que não está mais aqui'
                      : `por ${ban.bannedBy.displayName}`}
                    {` em ${formatDate(ban.createdAt)}`}
                  </span>
                </div>
                <button
                  type="button"
                  className="guild-dialog-cancel"
                  aria-label={`Revogar banimento de ${ban.user.displayName}`}
                  disabled={revoking === ban.user.id}
                  onClick={() => {
                    void handleRevoke(ban.user.id);
                  }}
                >
                  {revoking === ban.user.id ? 'Revogando…' : 'Revogar'}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="guild-dialog-actions">
          <button type="button" className="guild-dialog-cancel" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export default BansDialog;
